import {
    Activity,
    ArrowLeft,
    BellRing,
    CalendarRange,
    LayoutDashboard,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { getDashboard } from '~/api/monitoring'
import type { AdminDashboardDetail } from '~/api/types'
import {
    CATEGORICAL,
    CHART_AXIS,
    CHART_GRID,
    ChartCard,
    tooltipStyles,
} from '~/components/charts'
import {
    AlertActions,
    IndicatorTable,
    IngestHealth,
    NotifySettings,
    SourceMappings,
} from '~/components/DashboardInsights'
import { ReadingsManager } from '~/components/ReadingsManager'
import { StatCard } from '~/components/StatCard'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import { useToast } from '~/components/ui/toast'
import { formatDateTime } from '~/lib/utils'

// 모니터링 대시보드 상세 페이지(/dashboards/:id). 헤더 + 분석 StatCard +
// 측정값 시계열(라벨별 분리) + 알림 리스트. 상담 SessionDetailPage 와 동일한 상세 패턴.

const SEVERITY_TONES: Record<string, BadgeTone> = {
    critical: 'danger',
    high: 'danger',
    warning: 'warning',
    medium: 'warning',
    low: 'info',
    info: 'info',
}

const ALERT_STATUS_TONES: Record<string, BadgeTone> = {
    unread: 'warning',
    open: 'warning',
    acknowledged: 'info',
    resolved: 'success',
    closed: 'neutral',
}

const UNREAD_STATUSES = new Set(['unread', 'open'])

// ISO → 'M/D' (기간 보조표시).
function shortDay(iso: string | null): string {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return `${d.getMonth() + 1}/${d.getDate()}`
}

// ISO → 'M/D HH:mm' (시계열 축 라벨).
function shortTick(iso: string): string {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`
}

export function DashboardDetailPage() {
    const { id = '' } = useParams()
    const navigate = useNavigate()
    const toast = useToast()
    const [detail, setDetail] = useState<AdminDashboardDetail | null>(null)
    const [loading, setLoading] = useState(true)

    // 제어 액션(알림 처리·통지 저장·토큰 폐기) 성공 후 상세를 다시 불러와 상태/배지를 갱신한다.
    const load = useCallback(async () => {
        if (!id) return
        try {
            setDetail(await getDashboard(id))
        } catch (e) {
            toast.error((e as Error).message)
        }
    }, [id, toast])

    useEffect(() => {
        if (!id) return
        setLoading(true)
        load().finally(() => setLoading(false))
    }, [id, load])

    const readings = detail?.readings ?? []
    const alerts = detail?.alerts ?? []
    const unread = alerts.filter(
        (a) => a.status && UNREAD_STATUSES.has(a.status)
    ).length

    // 측정값을 recordedAt 기준 포인트로 피벗하고 라벨별 시리즈로 나눈다.
    const { points, seriesKeys } = useMemo(() => {
        const keys = [...new Set(readings.map((r) => r.label ?? '값'))]
        const byTime = new Map<string, Record<string, number | string>>()
        for (const r of readings) {
            const x = r.recordedAt ?? ''
            let p = byTime.get(x)
            if (!p) {
                p = { x }
                byTime.set(x, p)
            }
            if (r.value != null) p[r.label ?? '값'] = r.value
        }
        const sorted = [...byTime.values()].sort((a, b) =>
            String(a.x).localeCompare(String(b.x))
        )
        return { points: sorted, seriesKeys: keys }
    }, [readings])

    // 범례 클릭으로 특정 지표만 집중(격리)해서 본다. 지표 구성이 바뀌어 사라진 키면 무시한다.
    const [isolated, setIsolated] = useState<string | null>(null)
    const focus = isolated && seriesKeys.includes(isolated) ? isolated : null

    // 시리즈 표시명 한글화: label(=영문 field_id)을 지표 nameKo로 매핑한다(미매칭 시 원래 키).
    // dataKey/격리는 영문 키를 그대로 쓰고, 화면 표시(name·범례·툴팁)만 한글로 바꾼다.
    const labelByField = useMemo(() => {
        const m = new Map<string, string>()
        for (const ind of detail?.indicators ?? []) {
            if (ind.nameKo) m.set(ind.fieldId, ind.nameKo)
        }
        return m
    }, [detail])
    const labelKo = (key: string) => labelByField.get(key) ?? key

    const first = readings[0]?.recordedAt ?? null
    const last = readings[readings.length - 1]?.recordedAt ?? null
    const periodValue =
        first && last ? `${shortDay(first)}~${shortDay(last)}` : '-'

    return (
        <div className='space-y-5'>
            <div className='flex items-center gap-2'>
                <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => navigate(-1)}
                    aria-label='뒤로가기'>
                    <ArrowLeft />
                    뒤로
                </Button>
            </div>

            {loading ? (
                <DetailSkeleton />
            ) : !detail ? (
                <EmptyState
                    icon={LayoutDashboard}
                    title='대시보드를 찾을 수 없어요.'
                    description='삭제되었거나 접근 권한이 없는 대시보드예요.'
                    action={
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => navigate(-1)}>
                            뒤로가기
                        </Button>
                    }
                />
            ) : (
                <>
                    <header>
                        <h1 className='text-foreground text-lg font-semibold tracking-tight'>
                            {detail.name || '이름 없음'}
                        </h1>
                        <p className='text-muted-foreground mt-0.5 text-[13px]'>
                            {detail.assessmentId
                                ? `평가 ${detail.assessmentId.slice(0, 8)} · `
                                : ''}
                            {formatDateTime(detail.createdAt)}
                        </p>
                    </header>

                    <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
                        <StatCard
                            label='측정값'
                            value={detail.readingsCount}
                            icon={Activity}
                            tone='info'
                        />
                        <StatCard
                            label='알림'
                            value={`${unread} / ${alerts.length}`}
                            hint='미확인 / 전체'
                            icon={BellRing}
                            tone={unread > 0 ? 'danger' : 'default'}
                        />
                        <StatCard
                            label='측정 기간'
                            value={periodValue}
                            hint={
                                readings.length > 0
                                    ? `${readings.length}개 측정`
                                    : undefined
                            }
                            icon={CalendarRange}
                        />
                    </div>

                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                        <Card className='p-5'>
                            <h3 className='krds-title text-foreground mb-3'>
                                인제스트 건강도
                            </h3>
                            <IngestHealth detail={detail} />
                        </Card>
                        <Card className='p-5'>
                            <h3 className='krds-title text-foreground mb-3'>
                                통지 설정
                            </h3>
                            <NotifySettings detail={detail} onChanged={load} />
                        </Card>
                    </div>

                    <ChartCard
                        title='측정값 추이'
                        description={
                            seriesKeys.length > 1
                                ? '기록 시각별 측정값 시계열 · 범례를 클릭하면 특정 지표만 집중해서 봐요'
                                : '기록 시각별 측정값 시계열'
                        }
                        empty={points.length === 0}
                        emptyText='측정값이 없어요.'>
                        <ResponsiveContainer width='100%' height={280}>
                            <AreaChart
                                data={points}
                                margin={{
                                    top: 8,
                                    right: 12,
                                    bottom: 0,
                                    left: 0,
                                }}>
                                <defs>
                                    {seriesKeys.map((k, i) => (
                                        <linearGradient
                                            key={k}
                                            id={`grad-reading-${i}`}
                                            x1='0'
                                            y1='0'
                                            x2='0'
                                            y2='1'>
                                            <stop
                                                offset='0%'
                                                stopColor={
                                                    CATEGORICAL[
                                                        i % CATEGORICAL.length
                                                    ]
                                                }
                                                stopOpacity={0.24}
                                            />
                                            <stop
                                                offset='100%'
                                                stopColor={
                                                    CATEGORICAL[
                                                        i % CATEGORICAL.length
                                                    ]
                                                }
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid
                                    stroke={CHART_GRID}
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey='x'
                                    tickFormatter={shortTick}
                                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                                    axisLine={{ stroke: CHART_GRID }}
                                    tickLine={false}
                                    minTickGap={32}
                                />
                                <YAxis
                                    type='number'
                                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={40}
                                />
                                <Tooltip
                                    labelFormatter={(l) => shortTick(String(l))}
                                    contentStyle={tooltipStyles.contentStyle}
                                    itemStyle={tooltipStyles.itemStyle}
                                    labelStyle={tooltipStyles.labelStyle}
                                    cursor={{ stroke: CHART_GRID }}
                                />
                                {seriesKeys.length > 1 ? (
                                    <Legend
                                        wrapperStyle={{
                                            fontSize: 13,
                                            paddingTop: 8,
                                            cursor: 'pointer',
                                        }}
                                        iconType='plainline'
                                        onClick={(o) => {
                                            const key = String(
                                                (o as { dataKey?: string })
                                                    .dataKey ?? ''
                                            )
                                            if (key)
                                                setIsolated((cur) =>
                                                    cur === key ? null : key
                                                )
                                        }}
                                        formatter={(value, entry) => {
                                            const key = (
                                                entry as { dataKey?: string }
                                            )?.dataKey
                                            return (
                                                <span
                                                    style={{
                                                        opacity:
                                                            focus &&
                                                            focus !== key
                                                                ? 0.35
                                                                : 1,
                                                    }}>
                                                    {value}
                                                </span>
                                            )
                                        }}
                                    />
                                ) : null}
                                {seriesKeys.map((k, i) => (
                                    <Area
                                        key={k}
                                        type='monotone'
                                        dataKey={k}
                                        name={labelKo(k)}
                                        stroke={
                                            CATEGORICAL[i % CATEGORICAL.length]
                                        }
                                        strokeWidth={focus === k ? 2.5 : 2}
                                        fill={`url(#grad-reading-${i})`}
                                        dot={false}
                                        connectNulls
                                        isAnimationActive={false}
                                        hide={focus !== null && focus !== k}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <Card className='p-5'>
                        <h3 className='krds-title text-foreground mb-3'>
                            측정값
                        </h3>
                        <ReadingsManager
                            dashboardId={detail.id}
                            onChanged={load}
                        />
                    </Card>

                    <Card className='p-5'>
                        <h3 className='krds-title text-foreground mb-3'>
                            지표
                        </h3>
                        <IndicatorTable detail={detail} />
                    </Card>

                    <Card className='p-5'>
                        <h3 className='krds-title text-foreground mb-3'>
                            소스 매핑
                        </h3>
                        <SourceMappings detail={detail} />
                    </Card>

                    <Card className='p-5'>
                        <h3 className='krds-title text-foreground mb-3'>
                            알림 ({alerts.length})
                        </h3>
                        {alerts.length === 0 ? (
                            <EmptyState
                                icon={BellRing}
                                title='알림이 없어요.'
                                description='임계치를 벗어난 측정값이 생기면 여기에 표시돼요.'
                            />
                        ) : (
                            <ul className='divide-border divide-y'>
                                {alerts.map((a) => (
                                    <li
                                        key={a.id}
                                        className='flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0'>
                                        <div className='min-w-0'>
                                            <div className='flex flex-wrap items-center gap-1.5'>
                                                <span className='text-foreground text-[13px] font-medium'>
                                                    {a.title ||
                                                        a.alertType ||
                                                        '알림'}
                                                </span>
                                                {a.severity ? (
                                                    <Badge
                                                        tone={
                                                            SEVERITY_TONES[
                                                                a.severity
                                                            ] ?? 'neutral'
                                                        }>
                                                        {a.severity}
                                                    </Badge>
                                                ) : null}
                                                {a.status ? (
                                                    <Badge
                                                        tone={
                                                            ALERT_STATUS_TONES[
                                                                a.status
                                                            ] ?? 'neutral'
                                                        }>
                                                        {a.status}
                                                    </Badge>
                                                ) : null}
                                            </div>
                                            <p className='text-muted-foreground mt-0.5 text-[12px]'>
                                                {a.alertType ?? '-'}
                                                {a.fieldId
                                                    ? ` · ${a.fieldId}`
                                                    : ''}
                                                {a.itemTitle
                                                    ? ` · ${a.itemTitle}`
                                                    : ''}
                                            </p>
                                            {a.message ? (
                                                <p className='text-muted-foreground mt-0.5 text-[12px]'>
                                                    {a.message}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className='flex shrink-0 flex-col items-end gap-1.5 text-right'>
                                            <span className='text-muted-foreground block text-[12px] whitespace-nowrap'>
                                                {formatDateTime(a.createdAt)}
                                            </span>
                                            {a.updatedAt ? (
                                                <span className='text-muted-foreground/70 block text-[11px] whitespace-nowrap'>
                                                    갱신{' '}
                                                    {formatDateTime(
                                                        a.updatedAt
                                                    )}
                                                </span>
                                            ) : null}
                                            <AlertActions
                                                dashboardId={detail.id}
                                                alert={a}
                                                onChanged={load}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </>
            )}
        </div>
    )
}

function DetailSkeleton() {
    return (
        <div className='space-y-5'>
            <Skeleton className='h-7 w-64' />
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
                {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className='h-20' />
                ))}
            </div>
            <Skeleton className='h-72' />
            <Skeleton className='h-48' />
        </div>
    )
}
